import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../services/api";
import jsQR from "jsqr";
import "./ScanPage.css";

interface ScanHistory {
  time: string;
  nom: string;
  table: string;
}

interface StatsResponse {
  success: boolean;
  total: number;
  scannes: number;
}

interface ScanResponse {
  success: boolean;
  message: string;
  invite: {
    nom: string;
    table: string;
  };
}

const ScanPage: React.FC = () => {
  const { mariageId } = useParams<{ mariageId: string }>();
  const navigate = useNavigate();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>(0);
  const [scanning, setScanning] = useState(false);
  const [scanPaused, setScanPaused] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [manualName, setManualName] = useState("");
  const [message, setMessage] = useState<{ type: "success" | "error" | "info"; text: string } | null>(null);
  const [stats, setStats] = useState({ total: 0, scannes: 0 });
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [scanHistory, setScanHistory] = useState<ScanHistory[]>([]);
  const [lastScanned, setLastScanned] = useState<string | null>(null);

  useEffect(() => {
    const savedHistory = localStorage.getItem(`scanHistory_${mariageId}`);
    if (savedHistory) {
      try {
        setScanHistory(JSON.parse(savedHistory));
      } catch (e) {
        console.error("Erreur chargement historique:", e);
      }
    }
  }, [mariageId]);

  useEffect(() => {
    if (scanHistory.length > 0) {
      localStorage.setItem(`scanHistory_${mariageId}`, JSON.stringify(scanHistory));
    }
  }, [scanHistory, mariageId]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get<StatsResponse>(`/invites/mariages/${mariageId}/stats`);
      if (response.data.success) {
        setStats({ 
          total: response.data.total || 0, 
          scannes: response.data.scannes || 0 
        });
      }
    } catch (error) {
      console.error("Erreur chargement stats:", error);
    }
  }, [mariageId]);

  const startCamera = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: "environment" }
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setCameraActive(true);
        setCameraError(null);
        setScanPaused(false);
      }
    } catch (err) {
      console.error("Erreur accès caméra:", err);
      setCameraError("Impossible d'accéder à la caméra. Vérifiez les permissions.");
      setCameraActive(false);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
    }
    setCameraActive(false);
  }, []);

  const validateScan = useCallback(async (token: string) => {
    if (lastScanned === token) return;
    
    try {
      const response = await api.post<ScanResponse>(`/invites/scan/${token}`, {
        mariageId
      });
      
      fetchStats();
      
      if (response.data.success) {
        setLastScanned(token);
        
        const now = new Date();
        const timeStr = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
        
        setScanHistory(prev => [{
          time: timeStr,
          nom: response.data.invite.nom,
          table: response.data.invite.table
        }, ...prev].slice(0, 3));

        setMessage({ 
          type: "success", 
          text: `✅ ${response.data.invite.nom} - Table ${response.data.invite.table} - Confirmé !` 
        });
        
        setScanPaused(true);
        setScanning(false);

        setTimeout(() => setLastScanned(null), 3000);
        
        return true;
      } else {
        setMessage({ 
          type: "error", 
          text: response.data.message || "QR code invalide" 
        });
        
        setTimeout(() => {
          setScanPaused(false);
          setMessage(null);
        }, 2000);
        
        return false;
      }
    } catch (err: any) {
      fetchStats();
      
      setMessage({ 
        type: "error", 
        text: err.response?.data?.message || "Erreur lors de la validation" 
      });
      
      setTimeout(() => {
        setScanPaused(false);
        setMessage(null);
      }, 2000);
      
      return false;
    }
  }, [mariageId, lastScanned, fetchStats]);

  const scanQRCode = useCallback(() => {
    if (!videoRef.current || !cameraActive || !canvasRef.current || scanning || scanPaused) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    
    if (!context || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(scanQRCode);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = context.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, canvas.width, canvas.height, {
      inversionAttempts: "dontInvert",
    });
    
    if (code && code.data) {
      let token = code.data;
      
      if (token.includes('/invitation/')) {
        token = token.split('/invitation/')[1];
      }
      
      token = token.split('?')[0].split('#')[0];
      
      setScanning(true);
      validateScan(token);
    }

    animationRef.current = requestAnimationFrame(scanQRCode);
  }, [cameraActive, scanning, scanPaused, validateScan]);

  const handleNextScan = () => {
    setScanPaused(false);
    setMessage(null);
  };

  useEffect(() => {
    startCamera();
    
    return () => {
      stopCamera();
    };
  }, [startCamera, stopCamera]);

  useEffect(() => {
    if (cameraActive && videoRef.current && !scanPaused) {
      scanQRCode();
    }
  }, [cameraActive, scanPaused, scanQRCode]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manualName.trim()) return;
    
    try {
      const response = await api.post<ScanResponse>(`/invites/manual/${mariageId}`, {
        nom: manualName
      });
      
      fetchStats();
      
      if (response.data.success) {
        const now = new Date();
        const timeStr = `${now.getHours()}:${now.getMinutes().toString().padStart(2, '0')}`;
        
        setScanHistory(prev => [{
          time: timeStr,
          nom: response.data.invite.nom,
          table: response.data.invite.table
        }, ...prev].slice(0, 3));

        setMessage({ 
          type: "success", 
          text: `✅ ${response.data.invite.nom} - Table ${response.data.invite.table} - Confirmé (saisie manuelle)` 
        });
        
        setManualName("");
        setShowManual(false);
        setScanPaused(true);
      } else {
        if (response.data.message === "Aucun invité trouvé avec ce nom") {
          setMessage({ 
            type: "error", 
            text: `"${manualName}" n'existe pas dans la liste des invités` 
          });
        } else {
          setMessage({ 
            type: "error", 
            text: response.data.message || "Invitée non trouvé" 
          });
        }
      }
    } catch (error: any) {
      fetchStats();
      
      setMessage({ 
        type: "error", 
        text: error.response?.data?.message || "Erreur lors de la validation" 
      });
    }
  };

  return (
    <div className="scan-container">
      <canvas ref={canvasRef} style={{ display: 'none' }} />
      
      <div className="scan-header">
        <button className="back-btn" onClick={() => {
          stopCamera();
          navigate("/dashboard/receptionniste");
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor"/>
          </svg>
          Retour
        </button>
        <h1>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" fill="currentColor"/>
          </svg>
          Scanner les invitations
        </h1>
      </div>

      <div className="stats-card">
        <div className="stat-item">
          <span className="stat-label">Total invités</span>
          <span className="stat-value">{stats.total}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Scannés</span>
          <span className="stat-value">{stats.scannes}</span>
        </div>
        <div className="stat-item">
          <span className="stat-label">Restants</span>
          <span className="stat-value">{Math.max(0, stats.total - stats.scannes)}</span>
        </div>
      </div>

      <div className="scan-area">
        {!showManual ? (
          <>
            <div className="camera-container">
              {cameraError ? (
                <div className="camera-error">
                  <span className="error-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
                    </svg>
                  </span>
                  <p>{cameraError}</p>
                  <button onClick={startCamera} className="retry-camera">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
                    </svg>
                    Réessayer
                  </button>
                </div>
              ) : (
                <>
                  <video
                    ref={videoRef}
                    autoPlay
                    playsInline
                    className="camera-preview"
                  />
                  {scanning && (
                    <div className="scanning-overlay">
                      <div className="scanner-line"></div>
                      <p>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                        </svg>
                        Scan en cours...
                      </p>
                    </div>
                  )}
                  {scanPaused && !scanning && (
                    <div className="scanning-overlay success">
                      <p>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                        </svg>
                        Scan réussi !
                      </p>
                      <button onClick={handleNextScan} className="next-scan-btn">
                        Scanner suivant
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" fill="currentColor"/>
                        </svg>
                      </button>
                    </div>
                  )}
                  {!cameraActive && !cameraError && (
                    <div className="camera-loading">
                      <div className="loading-spinner"></div>
                      <p>Initialisation de la caméra...</p>
                    </div>
                  )}
                  <div className="scan-instruction">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
                    </svg>
                    <p>Placez le QR code devant la caméra</p>
                  </div>
                </>
              )}
            </div>

            <div className="scan-actions">
              <button 
                className="manual-btn"
                onClick={() => {
                  stopCamera();
                  setShowManual(true);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Saisie manuelle
              </button>
            </div>
          </>
        ) : (
          <div className="manual-form">
            <h3>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
              </svg>
              Saisie manuelle
            </h3>
            <form onSubmit={handleManualSubmit}>
              <input
                type="text"
                placeholder="Nom de l'invité"
                value={manualName}
                onChange={(e) => setManualName(e.target.value)}
                autoFocus
              />
              <div className="manual-actions">
                <button type="submit" className="confirm-btn">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                  </svg>
                  Confirmer
                </button>
                <button 
                  type="button" 
                  className="cancel-btn"
                  onClick={() => {
                    setShowManual(false);
                    startCamera();
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                  </svg>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        )}
      </div>

      {message && !scanPaused && (
        <div className={`message ${message.type}`}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            {message.type === "success" && (
              <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
            )}
            {message.type === "error" && (
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
            )}
            {message.type === "info" && (
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="currentColor"/>
            )}
          </svg>
          {message.text}
        </div>
      )}

      <div className="recent-scans">
        <h3>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
          </svg>
          3 derniers scans
        </h3>
        <div className="scans-list">
          {scanHistory.length === 0 ? (
            <p className="empty-scans">Aucun scan pour le moment</p>
          ) : (
            scanHistory.map((scan, index) => (
              <div key={index} className="scan-item">
                <span className="scan-time">{scan.time}</span>
                <span className="scan-name">{scan.nom}</span>
                <span className="scan-table">Table {scan.table}</span>
                <span className="scan-status success">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                  </svg>
                </span>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default ScanPage;